import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react"

import { Button } from "@/components/ui/button"
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
} from "@/components/ui/pagination"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { cn } from "@/lib/utils"
interface CallbackPaginationProps {
  pagination: {
    page: number
    pages: number
    limit: number
    count: number
    totalItems: number
  }
  onPageChange: (page: number) => void
  onPageSizeChange?: (size: number) => void
  isLoading?: boolean
  itemsPerPageOptions?: number[]
  defaultLimit?: number
}
interface PaginationRange {
  start: number
  end: number
  shouldShowLeftDots: boolean
  shouldShowRightDots: boolean
}
const SIBLING_COUNT = 1
const calculatePaginationRange = (
  currentPage: number,
  totalPages: number,
): PaginationRange => {
  let startPage = Math.max(1, currentPage - SIBLING_COUNT)
  let endPage = Math.min(totalPages, currentPage + SIBLING_COUNT)
  if (currentPage <= SIBLING_COUNT) {
    endPage = Math.min(5, totalPages)
  }
  if (currentPage > totalPages - SIBLING_COUNT) {
    startPage = Math.max(1, totalPages - 4)
  }
  return {
    start: startPage,
    end: endPage,
    shouldShowLeftDots: startPage > 2,
    shouldShowRightDots: endPage < totalPages - 1,
  }
}
const PaginationButton = ({
  page,
  currentPage,
  onClick,
  disabled,
}: {
  page: number | string
  currentPage: number
  onClick: (page: number) => void
  disabled?: boolean
}) => {
  if (page === "...") {
    return <PaginationEllipsis />
  }
  const pageNumber = page as number
  const isActive = pageNumber === currentPage
  return (
    <Button
      variant={isActive ? "default" : "ghost"}
      size="icon"
      onClick={() => onClick(pageNumber)}
      disabled={disabled}
      className={cn("h-8 w-8", isActive && "pointer-events-none")}
    >
      {pageNumber}
    </Button>
  )
}
export const CallbackPagination = ({
  pagination,
  onPageChange,
  onPageSizeChange,
  isLoading,
  itemsPerPageOptions = [10, 20, 30, 40, 50],
  defaultLimit = 10,
}: CallbackPaginationProps) => {
  const { page: currentPage, pages: totalPages, limit } = pagination
  const currentLimit = limit?.toString() || defaultLimit.toString()
  const range = calculatePaginationRange(currentPage, totalPages)
  const pageNumbers: (number | string)[] = []
  if (range.start > 1) {
    pageNumbers.push(1)
  }
  if (range.shouldShowLeftDots) {
    pageNumbers.push("...")
  }
  for (let i = range.start; i <= range.end; i++) {
    pageNumbers.push(i)
  }
  if (range.shouldShowRightDots) {
    pageNumbers.push("...")
  }
  if (range.end < totalPages) {
    pageNumbers.push(totalPages)
  }
  return (
    <div className="flex flex-col items-center justify-between gap-3 lg:relative">
      <Pagination>
        <PaginationContent>
          <PaginationItem>
            <Button
              onClick={() => onPageChange(Math.max(1, currentPage - 1))}
              variant="ghost"
              disabled={currentPage === 1 || isLoading}
              className="hidden md:flex"
            >
              <ChevronLeftIcon className="size-4" />
              Anterior
            </Button>
            <Button
              onClick={() => onPageChange(Math.max(1, currentPage - 1))}
              variant="ghost"
              size="icon"
              disabled={currentPage === 1 || isLoading}
              className="md:hidden"
            >
              <ChevronLeftIcon className="size-4" />
            </Button>
          </PaginationItem>
          <div className="flex">
            {pageNumbers.map((page, index) => (
              <PaginationItem key={index}>
                <PaginationButton
                  page={page}
                  currentPage={currentPage}
                  onClick={onPageChange}
                  disabled={isLoading}
                />
              </PaginationItem>
            ))}
          </div>
          <PaginationItem>
            <Button
              onClick={() =>
                onPageChange(Math.min(totalPages, currentPage + 1))
              }
              variant="ghost"
              className="hidden md:flex"
              disabled={
                currentPage === totalPages || isLoading || totalPages === 0
              }
            >
              Próximo
              <ChevronRightIcon className="size-4" />
            </Button>
            <Button
              onClick={() =>
                onPageChange(Math.min(totalPages, currentPage + 1))
              }
              variant="ghost"
              size="icon"
              disabled={
                currentPage === totalPages || isLoading || totalPages === 0
              }
              className="md:hidden"
            >
              <ChevronRightIcon className="size-4" />
            </Button>
          </PaginationItem>
        </PaginationContent>
      </Pagination>
      {onPageSizeChange && (
        <div className="flex w-[160px] items-center gap-2 lg:absolute lg:right-0">
          <p className="text-sm text-muted-foreground">Por página</p>
          <Select
            value={currentLimit}
            onValueChange={(v) => onPageSizeChange(Number(v))}
            disabled={isLoading}
          >
            <SelectTrigger className="h-8 w-[70px]">
              <SelectValue placeholder={defaultLimit.toString()} />
            </SelectTrigger>
            <SelectContent>
              {itemsPerPageOptions.map((value) => (
                <SelectItem key={value} value={value.toString()}>
                  {value}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
    </div>
  )
}
